import {
  ChartLine,
  ChevronRight,
  ClipboardCheck,
  Fuel,
  Menu,
  MessageCircle,
  Receipt,
  ShieldCheck,
} from 'lucide-react'
import { useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { AccountContent } from './AccountContent'
import { DEFAULT_ORG_NAME, Viewport } from './accountModel'
import { useAgentFace, useAgentName } from './agentName'
import { CfoOverview } from './CfoOverview'
import { ChatPane } from './ChatPane'
import { CFO_THREAD } from './contacts'
import { BlackMark } from './FleetTopNav'
import { PinnedArtifactsProvider } from './pinnedArtifacts'
import { navGroups, ProductTab, PROTOTYPE_TABS, TAB_LABEL } from './prototypeNav'
import { SignedInAvatar } from './SignedInAvatar'

/** The agent's own screen, which is where the phone opens. */
const AGENT = 'AGENT'

/** A product with no rows is a destination, not a group to expand. */
const HAS_PAGES = PROTOTYPE_TABS.filter((entry) => navGroups(entry).length > 0)

const TAB_ICON = {
  [ProductTab.INSURANCE]: ShieldCheck,
  [ProductTab.BOOKKEEPER]: Receipt,
  [ProductTab.CFO]: ChartLine,
  [ProductTab.COMPLIANCE]: ClipboardCheck,
  [ProductTab.FUEL]: Fuel,
}

/**
 * The web app on a phone under this placement. The agent takes the screen and
 * keeps its own bar along the bottom; the products are behind the menu, which
 * is the trade a phone forces — only one of the two can own the bottom edge.
 */
export function PlainMobilePlacement({
  className,
  notified,
  tabs = PROTOTYPE_TABS,
  tabLabels,
}: {
  className?: string
  /** The agent spoke first: the dot in the bar is the whole point of putting it there. */
  notified?: boolean
  /** Which products this account carries — the enum holds more than any one mock shows. */
  tabs?: ProductTab[]
  /** Renames products for this mock only. */
  tabLabels?: Partial<Record<ProductTab, string>>
}) {
  const [tab, setTab] = useState<ProductTab | typeof AGENT>(AGENT)
  const [page, setPage] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ProductTab | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const agentName = useAgentName()
  const face = useAgentFace()

  function go(next: ProductTab | typeof AGENT, nextPage: string | null = null) {
    setTab(next)
    setPage(nextPage)
    setMenuOpen(false)
  }

  return (
    <PinnedArtifactsProvider>
      <div className={cn('border-ink-100 relative flex flex-col overflow-hidden border bg-white', className)}>
        <div className="border-ink-100 flex shrink-0 items-center gap-2 border-b px-3 py-2">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
            className="hover:bg-ink-50 -ml-1 rounded p-1"
          >
            <Menu className="text-ink size-4" />
          </button>
          <BlackMark />
          <span className="text-ink truncate text-xs font-semibold">{DEFAULT_ORG_NAME}</span>
          <button
            type="button"
            onClick={() => go(AGENT)}
            aria-label={`Ask ${agentName}`}
            aria-current={tab === AGENT}
            className="relative ml-auto"
          >
            <Avatar
              className={cn(
                'size-7 rounded-md border-2 border-white shadow-sm',
                tab === AGENT && 'ring-brand-700 ring-2'
              )}
            >
              {face ? (
                <AvatarImage src={face} alt="" className="scale-125 object-cover" />
              ) : (
                <AvatarFallback className="bg-brand-100 rounded-md" />
              )}
            </Avatar>
            {notified && (
              <span
                className="bg-destructive absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-white"
                aria-label="1 unread message"
                role="status"
              />
            )}
          </button>
          <SignedInAvatar />
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {tab === AGENT ? (
            <ChatPane width="fill" hideContacts notified={notified} connect agentThread={CFO_THREAD} />
          ) : tab === ProductTab.CFO ? (
            <CfoOverview />
          ) : (
            <AccountContent viewport={Viewport.MOBILE} page={page ?? navGroups(tab)[0].rows[0].title} />
          )}
        </div>

        <button
          type="button"
          aria-label="Close menu"
          tabIndex={menuOpen ? 0 : -1}
          onClick={() => setMenuOpen(false)}
          className={cn(
            'bg-ink/20 absolute inset-0 z-20 transition-opacity duration-200 motion-reduce:transition-none',
            !menuOpen && 'pointer-events-none opacity-0'
          )}
        />
        <div
          className={cn(
            'no-scrollbar border-ink-100 absolute inset-y-0 left-0 z-30 flex w-56 flex-col gap-1 overflow-y-auto border-r bg-white p-2 shadow-xl transition-transform duration-200 ease-out motion-reduce:transition-none',
            !menuOpen && '-translate-x-full'
          )}
        >
          <MenuRow label={agentName} icon={MessageCircle} active={tab === AGENT} onSelect={() => go(AGENT)} />
          <span className="border-ink-100 my-1 border-t" />
          {tabs.map((entry) =>
            HAS_PAGES.includes(entry) ? (
              <ProductGroup
                key={entry}
                product={entry}
                label={tabLabels?.[entry] ?? TAB_LABEL[entry]}
                open={expanded === entry}
                onOpenChange={(next) => setExpanded(next ? entry : null)}
                activePage={tab === entry ? page : null}
                onSelectPage={(row) => go(entry, row)}
              />
            ) : (
              <MenuRow
                key={entry}
                label={tabLabels?.[entry] ?? TAB_LABEL[entry]}
                icon={TAB_ICON[entry]}
                active={tab === entry}
                onSelect={() => go(entry)}
              />
            )
          )}
        </div>
      </div>
    </PinnedArtifactsProvider>
  )
}

function MenuRow({
  label,
  icon: Icon,
  active,
  onSelect,
}: {
  label: string
  icon: typeof ShieldCheck
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active}
      className={cn(
        'flex items-center gap-2 rounded-md px-2 py-2 text-left text-xs',
        active ? 'bg-ink-50 text-ink font-semibold' : 'text-ink-500 hover:bg-ink-50'
      )}
    >
      <Icon className={cn('size-4', active && 'text-brand-700')} />
      <span className="truncate">{label}</span>
    </button>
  )
}

function ProductGroup({
  product,
  label,
  open,
  onOpenChange,
  activePage,
  onSelectPage,
}: {
  product: ProductTab
  label: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The row being shown, when this product is the one on screen. */
  activePage: string | null
  onSelectPage: (row: string) => void
}) {
  const Icon = TAB_ICON[product]

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger
        className={cn(
          'hover:bg-ink-50 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs',
          activePage ? 'text-ink font-semibold' : 'text-ink-500'
        )}
      >
        <Icon className={cn('size-4', activePage && 'text-brand-700')} />
        <span className="truncate">{label}</span>
        <ChevronRight
          className={cn('text-ink-400 ml-auto size-3.5 transition-transform', open && 'rotate-90')}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-0.5 py-1 pl-8">
          {navGroups(product)
            .flatMap((group) => group.rows)
            .map((row) => (
              <button
                key={row.title}
                type="button"
                onClick={() => onSelectPage(row.title)}
                aria-current={activePage === row.title}
                className={cn(
                  'truncate rounded-md px-2 py-1.5 text-left text-[11px]',
                  activePage === row.title
                    ? 'bg-ink-50 text-ink font-semibold'
                    : 'text-ink-500 hover:bg-ink-50'
                )}
              >
                {row.title}
              </button>
            ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
